import * as React from 'react';
import flagUS from '../imgs/flag-us.svg';
import flagCA from '../imgs/flag-ca.svg';

// A flag is display-only (Country): the option's `flag` ('us' | 'ca') picks the
// picture; only its value ('US' / 'CA') is ever committed.
const FLAGS = { us: flagUS, ca: flagCA };
function Flag({ code, style }) {
  const src = FLAGS[code];
  if (!src) return null;
  return <img src={src} alt="" aria-hidden="true" style={{ flex: '0 0 auto', width: 20, height: 14, display: 'block', objectFit: 'contain', borderRadius: 2, ...style }} />;
}

// Custom select — a native <select> can't be styled, so this is a button +
// absolutely-positioned list. Behavior (prefer-up placement, scroll-rail-on-
// mousedown, capture-phase document dismissal, keyboard nav) and every token
// below were read live off the design (claude-design MCP + render_preview
// getComputedStyle/DOM inspection), re-verified against screenshots.

const MENU_MAX = 265;
const MENU_FLOOR = 140;

export default function Select({
  id, label, options, value, onChange, error, wideMenu, menuNote, clearable = true,
}) {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const [up, setUp] = React.useState(false);
  const [menuMax, setMenuMax] = React.useState(MENU_MAX);
  const triggerRef = React.useRef(null);
  const listRef = React.useRef(null);

  // header/divider rows (the design's sticky "An estimate is fine..." caption
  // and its "Season"/"Month" group rules) aren't answers — keyboard nav and
  // selection only ever land on a real option.
  const pickable = options.filter((o) => !o.header && !o.divider);
  const sel = options.find((o) => String(o.value) === String(value));

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inSelect = e.target && e.target.closest && e.target.closest(`[data-select="${id}"]`);
      if (!inSelect) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, id]);

  const place = () => {
    const el = triggerRef.current;
    if (!el) return { up: false, menuMax: MENU_MAX };
    const r = el.getBoundingClientRect();
    const below = Math.max(0, window.innerHeight - r.bottom - 8);
    const above = Math.max(0, r.top - 60 - 8);
    const needed = Math.min(MENU_MAX, (options.length || 0) * 44 + 16);
    const wantsUp = above >= needed ? true : below >= needed ? false : above >= below;
    const room = wantsUp ? above : below;
    return { up: wantsUp, menuMax: Math.max(MENU_FLOOR, Math.min(MENU_MAX, room)) };
  };

  const openIt = () => {
    const p = place();
    setUp(p.up);
    setMenuMax(p.menuMax);
    setActive(Math.max(0, options.findIndex((o) => String(o.value) === String(value))));
    setOpen(true);
  };

  const pick = (opt) => { setOpen(false); onChange({ target: { value: opt.value } }); };

  const nudge = (dy) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (listRef.current) listRef.current.scrollBy({ top: dy, behavior: 'smooth' });
  };

  const step = (from, dir) => {
    let i = from;
    for (let n = 0; n < options.length; n += 1) {
      i += dir;
      if (i < 0 || i >= options.length) return from;
      if (!options[i].header && !options[i].divider) return i;
    }
    return from;
  };

  const onKeyDown = (e) => {
    const k = e.key;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(k)) { e.preventDefault(); openIt(); }
      return;
    }
    if (k === 'Escape' || k === 'Tab') { if (k === 'Escape') e.preventDefault(); setOpen(false); }
    else if (k === 'ArrowDown') { e.preventDefault(); setActive((a) => step(a, 1)); }
    else if (k === 'ArrowUp') { e.preventDefault(); setActive((a) => step(a, -1)); }
    else if (k === 'Enter') { e.preventDefault(); const o = options[active]; if (o && !o.header && !o.divider) pick(o); }
  };

  const scrollable = pickable.length > 5;
  const hasValue = !!sel;

  return (
    <div
      data-select={id}
      className={`ec-field ec-field--select ${hasValue || open ? 'has-value' : ''} ${error ? 'has-error' : ''}`.trim()}
      style={{ position: wideMenu ? 'static' : 'relative' }}
    >
      <div style={{ position: 'relative', minWidth: 0 }}>
        <button
          type="button"
          ref={triggerRef}
          className="ec-field-control"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
            borderColor: error ? 'var(--ec-border-error)' : open ? 'var(--ec-border-open)' : hasValue ? 'var(--ec-peach)' : 'var(--ec-field-border)',
            color: hasValue && !open ? 'var(--ec-text)' : undefined,
            fontWeight: hasValue && !open ? 500 : 400,
            paddingRight: clearable && hasValue && !open ? 46 : 38,
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => (open ? setOpen(false) : openIt())}
          onKeyDown={onKeyDown}
        >
          {sel && sel.flag && <Flag code={sel.flag} style={{ marginRight: 8 }} />}
          <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
            {sel ? sel.label : ''}
          </span>
          {hasValue && !open && sel.hint && (
            <span style={{ flex: '0 0 auto', marginLeft: 'auto', paddingLeft: 8, fontSize: 14, color: 'var(--ec-text)' }}>{sel.hint}</span>
          )}
        </button>
        {/* Pinned to the field's own vertical centre, like the label — NOT a
            flex child of the button: the button's content box starts below its
            21px top padding (5px bottom), so a centred flex child sat low
            whenever the label had floated up. Design: position:absolute;
            right:13px; top:50%; translateY(-50%) (+ rotate when open). */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', right: 13, top: '50%', display: clearable && hasValue && !open ? 'none' : 'flex', pointerEvents: 'none',
            transition: 'transform 0.18s ease', transform: `translateY(-50%)${open ? ' rotate(180deg)' : ''}`,
          }}
        >
          <svg width="14" height="9" viewBox="0 0 14 9" fill="none"><path d="M1 1.5l6 6 6-6" stroke="#333939" strokeWidth="2" fill="none" /></svg>
        </span>
        {clearable && hasValue && !open && (
          <button type="button" aria-label={`Clear ${label}`} onClick={(e) => { e.stopPropagation(); onChange({ target: { value: '' } }); }} style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', width: 26, height: 26, padding: 0, border: '1.5px solid var(--ec-peach)', borderRadius: 999,
            background: '#FFFFFF', cursor: 'pointer', zIndex: 2,
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="#231F1E" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        )}
        <span className="ec-field-label" style={hasValue && !open ? { color: 'var(--ec-text)' } : undefined}>{label}</span>
      </div>

      {open && (
        <div className="ec-select-menu" style={up ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }}>
          {scrollable && (
            <>
              <button type="button" aria-label="Scroll up" onMouseDown={nudge(-144)}
                style={{
                  position: 'absolute', top: 0, right: 0, zIndex: 2, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', width: 18, height: 18, padding: 0, border: 'none',
                  borderBottom: '1px solid var(--ec-rail-track-border)', background: 'var(--ec-rail-track)', cursor: 'pointer',
                }}>
                <svg width="9" height="6" viewBox="0 0 9 6" fill="none"><path d="M1 5l3.5-4L8 5" stroke="var(--ec-rail-thumb)" strokeWidth="1.6" strokeLinecap="round" fill="none" /></svg>
              </button>
              <button type="button" aria-label="Scroll down" onMouseDown={nudge(144)}
                style={{
                  position: 'absolute', bottom: 0, right: 0, zIndex: 2, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', width: 18, height: 18, padding: 0, border: 'none',
                  borderTop: '1px solid var(--ec-rail-track-border)', background: 'var(--ec-rail-track)', cursor: 'pointer',
                }}>
                <svg width="9" height="6" viewBox="0 0 9 6" fill="none"><path d="M1 1l3.5 4L8 1" stroke="var(--ec-rail-thumb)" strokeWidth="1.6" strokeLinecap="round" fill="none" /></svg>
              </button>
            </>
          )}
          <ul ref={listRef} className="ec-select-list" style={{ maxHeight: menuMax }}>
            {options.map((o, i) => {
              if (o.header) {
                return (
                  <li key={o.value} role="presentation" style={{
                    position: 'sticky', top: -6, zIndex: 1, display: 'flex', alignItems: 'center',
                    minHeight: 38, margin: '-6px -6px 6px', padding: '0 14px', background: '#231F1E',
                    borderRadius: '9px 9px 0 0', color: '#FFFFFF', fontSize: 12, fontWeight: 500,
                    letterSpacing: '0.12em', textTransform: 'uppercase', pointerEvents: 'none',
                  }}>
                    {o.caption}
                  </li>
                );
              }
              if (o.divider) {
                return (
                  <li key={o.value} role="presentation" style={{
                    display: 'flex', alignItems: 'center', gap: 10, minHeight: 26, margin: '2px 6px 0',
                    padding: '0 8px', pointerEvents: 'none',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ec-label)' }}>{o.caption}</span>
                    <span style={{ flex: '1 1 auto', height: 1, background: 'var(--ec-border)' }} />
                  </li>
                );
              }
              return (
                <li
                  key={o.value}
                  className={`ec-select-row ${String(o.value) === String(value) ? 'is-selected' : ''} ${active === i ? 'is-active' : ''}`.trim()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {o.flag && <Flag code={o.flag} />}
                    <span>{o.menuLabel || o.label}</span>
                  </span>
                  {o.hint && <span style={{ flex: '0 0 auto', marginLeft: 'auto', paddingLeft: 14, fontSize: 14, color: 'var(--ec-label)' }}>{o.hint}</span>}
                  <span aria-hidden="true" style={{ flex: '0 0 auto', fontSize: 15, color: 'var(--ec-text)', visibility: String(o.value) === String(value) ? 'visible' : 'hidden' }}>✓</span>
                </li>
              );
            })}
          </ul>
          {menuNote && <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--ec-label)', borderTop: '1px solid var(--ec-border)' }}>{menuNote}</div>}
        </div>
      )}
      {error && <div className="ec-field-error">{error}</div>}
    </div>
  );
}
